/**
 * Helpers for the publication Description field.
 *
 * The Description is authored in a rich text editor, so its value is always HTML
 * (even "hello" is stored as `<p>hello</p>`). Two concerns fall out of that:
 *
 * 1. Emptiness — an "empty" editor still emits markup like `<p></p>` or
 *    `<p><br></p>`, so a plain `value === ''` check no longer detects an empty
 *    field. Use {@link isRichTextEmpty}.
 *
 * 2. AWS WAF — the `CrossSiteScripting_BODY` rule rejects any request body that
 *    contains HTML with a 403 (surfaced in the browser as a CORS error). We
 *    Base64-encode the HTML before sending so the markup is hidden from the WAF;
 *    the backend's `Base64DecodingDeserializer` decodes it back to the original
 *    HTML (falling back to plain text if the value is not Base64). Use
 *    {@link encodeDescriptionForWaf}.
 */

/**
 * Returns true when the rich text value has no meaningful content — i.e. it is
 * null/empty or contains only markup/whitespace such as `<p></p>`,
 * `<p><br></p>` or `<p>&nbsp;</p>`.
 */
export const isRichTextEmpty = (html?: string | null): boolean => {
    if (!html) {
        return true;
    }
    const textOnly = html
        .replace(/<[^>]*>/g, '') // strip tags
        .replace(/&nbsp;/gi, ' ') // non-breaking spaces -> space
        .replace(/\s+/g, '') // drop all remaining whitespace
        .trim();
    return textOnly.length === 0;
};

/**
 * Base64-encodes the (UTF-8) rich text HTML so it passes the AWS WAF body
 * inspection. Produces standard RFC-4648 Base64 — byte-for-byte compatible with
 * Java's `Base64.getDecoder()` used by the backend deserializer.
 *
 * Empty/blank content is sent through as `null` so the backend stores nothing
 * rather than an encoded empty paragraph.
 */
export const encodeDescriptionForWaf = (description?: string | null): string | null => {
    if (isRichTextEmpty(description)) {
        return null;
    }
    const bytes = new TextEncoder().encode(description as string);
    let binary = '';
    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });
    return btoa(binary);
};
